import { useCallback, useEffect, useMemo, useState } from 'react'
import { BulkContentPost } from 'services/compose/state'

/** Selection state for the bulk posts list — selection follows the visible posts and prunes ids of removed posts. */
export function useBulkSelection(posts: BulkContentPost[]) {
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([])

  const postsMap = useMemo(() => posts.reduce((map: Record<string, BulkContentPost>, post) => {
    map[post.id] = post
    return map
  }, {}), [posts])

  useEffect(() => {
    setSelectedPostIds(current => {
      if (current.some(id => !postsMap[id])) {
        return current.filter(id => postsMap[id])
      }
      return current
    })
  }, [postsMap])

  const toggleSelected = useCallback((id: string) => {
    setSelectedPostIds(current => {
      const index = current.indexOf(id)
      return index === -1 ? [...current, id] : current.filter(postId => postId !== id)
    })
  }, [])

  const toggleAllSelected = useCallback(() => {
    setSelectedPostIds(current => (current.length < posts.length ? posts.map(p => p.id) : []))
  }, [posts])

  const removeFromSelection = useCallback((id: string) => {
    setSelectedPostIds(current => current.filter(postId => postId !== id))
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedPostIds([])
  }, [])

  return {
    selectedPostIds,
    postsMap,
    allSelected: selectedPostIds.length === posts.length,
    toggleSelected,
    toggleAllSelected,
    removeFromSelection,
    clearSelection
  }
}
